/** Every report in the hub: where it lives, what it shows and who may open it. */

export type ReportGroup = 'sales' | 'purchases' | 'profit' | 'stock' | 'parties' | 'financial';

export interface ReportInfo {
  slug: string;
  group: ReportGroup;
  title: string;
  description: string;
  /** All of these permissions are needed. */
  perms: string[];
}

export const GROUPS: { key: ReportGroup; title: string; subtitle: string }[] = [
  { key: 'sales', title: 'Sales', subtitle: 'What you sold, to whom and when' },
  { key: 'purchases', title: 'Purchases', subtitle: 'What you bought from suppliers' },
  { key: 'profit', title: 'Profit', subtitle: 'Margins from the books' },
  { key: 'stock', title: 'Stock', subtitle: 'What is on the shelves and what it is worth' },
  { key: 'parties', title: 'Parties & payments', subtitle: 'Who owes whom, and money in and out' },
  {
    key: 'financial',
    title: 'Financial statements',
    subtitle: 'Profit and loss, balance sheet and trial balance',
  },
];

const VIEW = ['reports.view'];
const COST = ['reports.view', 'products.view_cost'];
const FIN = ['reports.financial'];
const PAY = ['reports.view', 'payments.view'];

export const REPORTS: ReportInfo[] = [
  {
    slug: 'sales-by-period',
    group: 'sales',
    title: 'Sales over time',
    description: 'Invoiced sales by day, week or month',
    perms: VIEW,
  },
  {
    slug: 'sales-by-customer',
    group: 'sales',
    title: 'Sales by customer',
    description: 'Your biggest customers for the period',
    perms: VIEW,
  },
  {
    slug: 'sales-by-product',
    group: 'sales',
    title: 'Sales by product',
    description: 'Revenue and quantity per product or variant',
    perms: VIEW,
  },
  {
    slug: 'best-sellers',
    group: 'sales',
    title: 'Best sellers',
    description: 'Top items by quantity or revenue',
    perms: VIEW,
  },
  {
    slug: 'purchases-by-period',
    group: 'purchases',
    title: 'Purchases over time',
    description: 'Bills or goods received by day, week or month',
    perms: COST,
  },
  {
    slug: 'purchases-by-supplier',
    group: 'purchases',
    title: 'Purchases by supplier',
    description: 'How much you bought from each supplier',
    perms: COST,
  },
  {
    slug: 'purchases-by-product',
    group: 'purchases',
    title: 'Purchases by product',
    description: 'Cost and quantity bought per item',
    perms: COST,
  },
  {
    slug: 'gross-profit-by-period',
    group: 'profit',
    title: 'Gross profit over time',
    description: 'Revenue, cost of goods and margin by period',
    perms: FIN,
  },
  {
    slug: 'gross-profit-by-product',
    group: 'profit',
    title: 'Gross profit by product',
    description: 'Which items earn the most',
    perms: FIN,
  },
  {
    slug: 'stock-on-hand',
    group: 'stock',
    title: 'Stock on hand',
    description: 'Quantities, reservations and value per item',
    perms: VIEW,
  },
  {
    slug: 'low-stock',
    group: 'stock',
    title: 'Low stock',
    description: 'Items at or below their reorder level',
    perms: VIEW,
  },
  {
    slug: 'stock-valuation',
    group: 'stock',
    title: 'Stock valuation',
    description: 'Stock value per location vs the books',
    perms: FIN,
  },
  {
    slug: 'receivables-aging',
    group: 'parties',
    title: 'Receivables aging',
    description: 'What customers owe, by days overdue',
    perms: PAY,
  },
  {
    slug: 'payables-aging',
    group: 'parties',
    title: 'Payables aging',
    description: 'What you owe suppliers, by days overdue',
    perms: PAY,
  },
  {
    slug: 'payments',
    group: 'parties',
    title: 'Payments summary',
    description: 'Money in and out by method and purpose',
    perms: PAY,
  },
  {
    slug: 'cash-movement',
    group: 'parties',
    title: 'Cash & bank movement',
    description: 'Opening, inflows, outflows and closing per account',
    perms: FIN,
  },
  {
    slug: 'profit-and-loss',
    group: 'financial',
    title: 'Profit & loss',
    description: 'Income, expenses and net profit for a period',
    perms: FIN,
  },
  {
    slug: 'balance-sheet',
    group: 'financial',
    title: 'Balance sheet',
    description: 'Assets, liabilities and equity on a date',
    perms: FIN,
  },
  {
    slug: 'trial-balance',
    group: 'financial',
    title: 'Trial balance',
    description: 'Debit and credit balances of every account',
    perms: FIN,
  },
];

export function reportBySlug(slug: string): ReportInfo {
  const r = REPORTS.find((x) => x.slug === slug);
  if (!r) throw new Error(`Unknown report ${slug}`);
  return r;
}

export function canOpen(report: ReportInfo, can: (...perms: string[]) => boolean): boolean {
  return can(...report.perms);
}

/** Groups with their reports, in hub order. */
export function groupedReports(): { group: (typeof GROUPS)[number]; reports: ReportInfo[] }[] {
  return GROUPS.map((group) => ({
    group,
    reports: REPORTS.filter((r) => r.group === group.key),
  }));
}
